import './DatasetDetailsPreviewHeader.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleLeft, faAngleRight, faDownload, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TranslationKey, useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext } from 'react';
import { S_Link } from '../../../../design/links/S_Link';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { ImageNavigation } from '../../../components/ImageNavigation';
import { EnableFor } from '../../../containers/EnableForRole';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { doNothing } from '../../../helpers/function.helpers';
import { InputStatus } from '../../../models/error.model';
import { DatasetDetails } from '../DatasetDetails.context';
import { useDatasetPermissions } from '../datasets.permissions';
import { DatasetStatus } from '../datasetStatus.model';
import { EditableImagePreviewHeader } from './EditableImagePreviewHeader';

library.add(faAngleLeft, faAngleRight, faTimes, faDownload);

interface IDatasetDetailsImageInfoProps {
  total: number;
  name: string;
  current: number;
  isLoading: boolean;
  isHighResImageLoading: boolean;
  imageSize: number;
  imageWidth: number;
  channels: number;
  imageHeight: number;
  isCached: boolean;
  createdDate: string;
  downloadSpeed?: number;
  nextLink: string;
  previousLink: string;
  closeButtonLink: string;
  datasetName: string;
  datasetStatus: DatasetStatus;
  isDeleteModalShown: boolean;
  hasAnnotations: boolean;
  isLocked: boolean;
  nameStatus: InputStatus;
  newName: string;
  onToggleDeleteModal: () => void;
  onDeleteImage: () => Promise<void>;
  onDownloadImage: () => Promise<void>;
  onImageNameChange: (imageName: string) => void;
  onImageNameBlur: () => Promise<void>;
  onCancelImageRename: () => void;
}

export const DatasetDetailsPreviewHeader = observer((props: IDatasetDetailsImageInfoProps): ReactElement => {
  const { t } = useTranslator();
  const datasetsPermissions = useDatasetPermissions();
  const context = useContext(DatasetDetails);

  const { total, current, nextLink, previousLink, closeButtonLink } = props;
  let confirmationQuestion: TranslationKey = 'delete_image_confirmation_body';

  if (props.hasAnnotations) {
    confirmationQuestion = 'delete_used_images_confirmation_body';
  } else if (props.isLocked) {
    confirmationQuestion = 'delete_locked_images_confirmation_body';
  }

  const attributes = [
    { name: t('image_attribute_size'), value: `${props.imageSize.toFixed(3)} MB` },
    { name: t('image_attribute_resolution'), value: `${props.imageWidth} x ${props.imageHeight}` },
    { name: t('image_attribute_type'), value: `${props.channels} ${t('image_attribute_channels')}` },
    { name: t('image_attribute_status'), value: props.isCached ? t('image_attribute_status_cached') : t('image_attribute_status_not_cached') },
    { name: t('image_attribute_created'), value: toLocaleDateTimeString(props.createdDate) },
    {
      name: t('image_attribute_load_speed'),
      value: props.downloadSpeed ? `${props.downloadSpeed.toFixed(3)} Mbps` : t('image_attribute_load_speed_cached'),
    },
  ];

  return (
    <div className='dataset-image-preview-header'>
      <div className='image-name'>
        <EditableImagePreviewHeader
          name={props.name}
          datasetName={props.datasetName}
          datasetStatus={props.datasetStatus}
          onImageNameChange={props.onImageNameChange}
          onImageNameBlur={props.onImageNameBlur}
          nameStatus={props.nameStatus}
          canRenameImages={datasetsPermissions.canRenameImages(props.datasetStatus, context.store.details.createdById)}
          newName={props.newName}
          onCancelRenameImage={props.onCancelImageRename}
          onStartEdit={doNothing}
        />
        <span className='info'>{!props.isHighResImageLoading && attributes.map((x) => `${x.name}: ${x.value}`).join(' | ')}</span>
      </div>
      <div className='navigation'>
        <EnableFor hasPermission={datasetsPermissions.canDownloadImages(props.datasetStatus, context.store.details.createdById)}>
          <IconButton
            color='primary'
            variant='solid'
            title={t('download_image')}
            onClick={() => {
              void props.onDownloadImage();
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
          </IconButton>
        </EnableFor>
        <EnableFor
          hasPermission={
            props.hasAnnotations
              ? datasetsPermissions.canDeleteMarkedImages()
              : datasetsPermissions.canDeleteUnmarkedImages(props.datasetStatus, context.store.details.createdById)
          }
        >
          <IconButton
            color='primary'
            variant='outlined'
            title={t('delete_image')}
            onClick={props.onToggleDeleteModal}
          >
            <TrashIcon />
          </IconButton>
        </EnableFor>
        <ImageNavigation
          disabled={props.nameStatus.errorCodes.length > 0}
          current={current}
          total={total}
          nextLink={nextLink}
          previousLink={previousLink}
          isLoading={props.isLoading}
        />
        <S_Link
          color='text'
          to={closeButtonLink}
        >
          <IconButton
            color='primary'
            variant='solid'
            disabled={props.nameStatus.errorCodes.length > 0}
            title={t('close')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </S_Link>
      </div>
      <ConfirmationModal
        confirmationHeader={'delete_image_confirmation'}
        confirmationQuestion={confirmationQuestion}
        onCancel={props.onToggleDeleteModal}
        onConfirm={props.onDeleteImage}
        showModal={props.isDeleteModalShown}
        confirmationYes={'delete'}
      />
    </div>
  );
});
